import { useQuery } from '@tanstack/react-query';
import { featureAccessService } from '@/services/api/feature-access.service';
import { useClubAdmin } from '@/hooks/useClubAdmin';
import { PATH_TO_FEATURE_KEY } from '@/types/feature-access';

/**
 * Hook to fetch and manage feature access for the current club
 * Returns a map of feature_key -> is_enabled
 */
export function useClubFeatureAccess() {
  const { currentClub } = useClubAdmin();

  return useQuery({
    queryKey: ['club-feature-access', currentClub?.id],
    queryFn: async () => {
      if (!currentClub?.id) {
        return {};
      }
      return featureAccessService.getClubFeatureAccess(currentClub.id);
    },
    enabled: !!currentClub?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to check if a specific feature is enabled for the current club
 * @param featureKey - The feature key to check
 */
export function useFeatureEnabled(featureKey: string) {
  const { data: featureAccess, isLoading } = useClubFeatureAccess();

  return {
    isEnabled: featureAccess?.[featureKey] ?? false,
    isLoading,
  };
}

/**
 * Hook to check if a route path is accessible for the current club
 * @param path - The route path (e.g., '/club-admin/bookings')
 */
export function usePathAccess(path: string) {
  const featureKey = PATH_TO_FEATURE_KEY[path];
  return useFeatureEnabled(featureKey);
}
